import { Link } from "@inertiajs/react"
import { ExternalLink, SquareKanban, User, type LucideIcon } from "lucide-react"
import { useMemo } from "react"
import { PersonAvatar } from "@/components/people/person-avatar"
import { useTaskMentionTexts } from "@/components/tasks/task-mentions"
import { useTranslation } from "@/hooks/use-translation"
import { noteRelatedObjects, type NoteRelatedObject } from "@/lib/note-related-objects"
import { beginShellNavOnClick } from "@/lib/shell-nav-pending"
import { TASK_ASSET_ICON } from "@/lib/task-agents"
import type { TaskMentionDirectory } from "@/lib/task-mentions"
import { cn } from "@/lib/utils"
import type { TaskAssetKind, TaskMentionEntityKind } from "@/types/tasks"

const ENTITY_ASSET: Record<TaskMentionEntityKind, TaskAssetKind> = {
  hero: "character",
  loc: "location",
  prop: "prop",
  shot: "shot",
  script: "script",
}

const ROW_CLASS =
  "flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1.5 text-left focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"

type NoteRelatedObjectsProps = {
  body: string
  directory: TaskMentionDirectory
  currentUrl: string
}

export function NoteRelatedObjects({ body, directory, currentUrl }: NoteRelatedObjectsProps) {
  const { t } = useTranslation("notes")
  const texts = useTaskMentionTexts()
  const rows = useMemo(
    () => noteRelatedObjects(body, directory, texts, currentUrl),
    [body, currentUrl, directory, texts],
  )
  if (rows.length === 0) return null

  return (
    <section className="mt-4 border-t border-ui-border pt-3">
      <div className="flex h-7 items-center">
        <span className="text-xs leading-4 text-ui-text-muted">{t("relatedObjects")}</span>
      </div>
      <ul className="-mx-2 mt-1 flex flex-col gap-0.5">
        {rows.map((row) => (
          <li key={row.key} className="flex min-w-0 items-center rounded-lg hover:bg-ui-surface-raised">
            <RelatedRow row={row} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function RelatedRow({ row }: { row: NoteRelatedObject }) {
  const content = (
    <>
      <RelatedThumb row={row} />
      <span className={cn("min-w-0 flex-1 truncate text-xs", row.href ? "text-ui-text-primary" : "text-ui-text-muted")}>
        {row.label}
      </span>
      {row.subtitle ? (
        <span className="max-w-36 shrink-0 truncate text-[11px] text-ui-text-muted">{row.subtitle}</span>
      ) : null}
      {row.href ? <ExternalLink className="size-3.5 shrink-0 text-ui-text-muted" aria-hidden /> : null}
    </>
  )
  const href = row.href
  if (!href) return <div className={ROW_CLASS}>{content}</div>
  return (
    <Link
      href={href}
      title={row.openLabel ?? undefined}
      aria-label={linkLabel(row)}
      className={ROW_CLASS}
      onClick={(event) => beginShellNavOnClick(event, href)}
    >
      {content}
    </Link>
  )
}

function linkLabel(row: NoteRelatedObject): string {
  return row.openLabel ? `${row.openLabel}: ${row.label}` : row.label
}

function RelatedThumb({ row }: { row: NoteRelatedObject }) {
  if (row.kind === "person" && row.person) return <PersonAvatar person={row.person} size={28} />
  if (row.thumbUrl) {
    return (
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border bg-ui-surface-sunken",
          row.frontView && "p-0.5",
        )}
      >
        <img
          src={row.thumbUrl}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className={cn("size-full", row.frontView ? "object-contain" : "object-cover")}
        />
      </span>
    )
  }
  const Icon = iconFor(row)
  return (
    <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border bg-ui-surface-sunken text-ui-text-muted">
      <Icon className="size-3.5" aria-hidden />
    </span>
  )
}

function iconFor(row: NoteRelatedObject): LucideIcon {
  if (row.kind === "person") return User
  if (row.kind === "task") return SquareKanban
  return TASK_ASSET_ICON[ENTITY_ASSET[row.kind]]
}
